<script setup>
import { formatDecimals } from '@/helpers/stringHelpers'
import { calculateInvoiceTotal } from '@/utils/revenueHelpers'
import { computed, defineProps } from 'vue'

const props = defineProps({
  items: {
    type: Array,
    default: () => []
  },
  singleFreight: {
    type: Boolean,
    default: false
  },
  freightFilterInfo: {
    type: Object,
    default: null
  },
  freightCode: {
    type: Function,
    required: true
  },
  priceMode: {
    type: String,
    default: 'transport',
    validator: (value) => ['transport', 'revenue'].includes(value)
  }
})

const baseHeaderClass = 'blue-grey darken-1 white--text'

const priceHeaderText = computed(() => {
  return props.priceMode === 'revenue' ? 'Invoice Total ($)' : 'Price ($)'
})

const headers = computed(() => {
  const baseHeaders = [
    {
      text: 'Customer',
      value: 'customer',
      sortable: false,
      cellClass: 'text-no-wrap'
    },
    { text: 'Weight (kg)', value: 'weight', align: 'end', sortable: false },
    { text: 'Cartons', value: 'cartons', align: 'end', sortable: false },
    { text: 'Pigs', value: 'pigs', align: 'end', sortable: false },
    {
      text: priceHeaderText.value,
      value: 'price',
      align: 'end',
      sortable: false
    }
  ].map((header) => ({
    ...header,
    class: baseHeaderClass
  }))

  if (props.singleFreight) {
    baseHeaders.push({
      text: `Price ($) - ${props.freightFilterInfo?.code || '-'}`,
      value: 'filteredPrice',
      align: 'end',
      sortable: false,
      class: baseHeaderClass
    })
  }

  baseHeaders.push(
    {
      text: 'Delivered By',
      value: 'deliveredBy',
      sortable: false,
      class: baseHeaderClass
    },
    {
      text: 'Invoice Nr.',
      value: 'invoice',
      sortable: false,
      class: baseHeaderClass
    }
  )

  return baseHeaders
})

function getTotalPrice(item) {
  if (props.priceMode === 'revenue') {
    return calculateInvoiceTotal(item.loaded_items)
  }

  return Number(item.job_description?.total_ex_GST || 0)
}

const freightFilterTotal = (total) => {
  if (!total) return ''
  if (isNaN(total)) return 0

  return formatDecimals(total)
}
</script>

<template>
  <v-data-table
    :headers="headers"
    :items="items"
    item-key="id"
    dense
    hide-default-footer
    disable-pagination
    class="elevation-1"
  >
    <template #[`item.customer`]="{ item }">
      {{ item.destination.name }}
    </template>

    <template #[`item.weight`]="{ item }">
      {{ formatDecimals(item.job_description.total_weight) }}
    </template>

    <template #[`item.cartons`]="{ item }">
      {{ item.job_description.total_cartons }}
    </template>

    <template #[`item.pigs`]="{ item }">
      {{ item.job_description.total_pigs }}
    </template>

    <template #[`item.price`]="{ item }">
      {{ formatDecimals(getTotalPrice(item)) }}
    </template>

    <template #[`item.filteredPrice`]="{ item }">
      {{ freightFilterTotal(item.job_description.filteredPrice) }}
    </template>

    <template #[`item.deliveredBy`]="{ item }">
      {{ freightCode(item.delivery_co_ids) }}
    </template>

    <template #[`item.invoice`]="{ item }">
      {{ item.invoice || '-' }}
    </template>
  </v-data-table>
</template>
